import React, { useState, useEffect } from 'react';
import { useInjection } from 'services/ioc/ioc.react';
import { TaggingStore } from 'stores/Tagging/TaggingStore';
import { ServiceTypes } from 'constants/ServiceTypes';
import { observer } from 'mobx-react-lite';
import { IPlayer } from 'interfaces/team/IPlayer';
import { SearchBar } from 'components/ui/SearchBar/SearchBar';
import { Play } from 'models/tagging/Play';
import { PlayerSearchService, IApiLineupPlayer } from '@trackman/baseball-shared-components';
import { SectionHeader } from '../../SectionHeader/SectionHeader';
import { SubstitutionPlayer } from './SubstitutionPlayer';
import { SubstitutionsPlayersWrapper } from './SubstitutionsStyles';

interface IProps {
  refHeight: number;
  isPitcherSubstitution?: boolean;
  onClickPlayerCallback: (player: IPlayer) => void;
  onClickBackCallback?: () => void;
  substitutionPlayer?: IApiLineupPlayer;
  play: Play;
}

export const Substitutions: React.FC<IProps> = observer((props) => {
  const taggingStore = useInjection<TaggingStore>(ServiceTypes.TaggingStore);
  const { onClickPlayerCallback, onClickBackCallback, substitutionPlayer, refHeight, isPitcherSubstitution, play } = props;
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [substitutions, setSubstitutions] = useState<IPlayer[]>();
  const [substitutionsList, setSubstitutionsList] = useState<Array<IPlayer>>();
  const [searchService, setSearchService] = useState<PlayerSearchService | undefined>(undefined);

  useEffect(() => {
    filterSubstitutions();
    taggingStore.isSubstituting = true;
    return () => {
      taggingStore.isSubstituting = false;
    };
  }, []);

  useEffect(() => {
    substitutions && setSearchService(new PlayerSearchService(substitutions));
    setSubstitutionsList(substitutions);
  }, [substitutions]);

  useEffect(() => {
    searchSubstitutionsList(searchQuery);
  }, [searchQuery]);

  const searchSubstitutionsList = (query: string) => {
    searchService && setSubstitutionsList(searchService.searchPlayersByName(query));
  };

  const getScrollStyle = () => {
    const maxHeight = isPitcherSubstitution ? refHeight - 48 : refHeight - 115;
    const scrollStyle = { maxHeight };
    return scrollStyle;
  };

  const filterSubstitutions = () => {
    let lineupIds: string[] = [];
    let roster: IPlayer[] = [];

    if (isPitcherSubstitution) {
      lineupIds = play.getPitchingTeamLineup().players.map((p) => {
        return p.id;
      });
      roster = play.isHomeTeamBatting ? taggingStore.awayTeamRoster : taggingStore.homeTeamRoster;
    } else {
      lineupIds = play.getBattingTeamLineup().players.map((p) => {
        return p.id;
      });
      roster = play.isHomeTeamBatting ? taggingStore.homeTeamRoster : taggingStore.awayTeamRoster;
    }

    roster = roster.filter((p) => {
      return lineupIds.indexOf(p.id) < 0;
    });

    setSubstitutions(roster);
  };

  return (
    <div>
      <SectionHeader
        title={`You're substituting ${substitutionPlayer && substitutionPlayer.nameRef}`}
        hasBackIcon
        onClickBackIconCallback={onClickBackCallback}
      />
      <SearchBar
        searchQuery={searchQuery}
        setSearchQueryCallback={(value: string) => setSearchQuery(value)}
        placeholder='Search for player'
      />
      <SubstitutionsPlayersWrapper style={getScrollStyle()}>
        {substitutionsList &&
          substitutionsList.map((player, index) => {
            return (
              <SubstitutionPlayer
                key={index}
                player={player}
                onClickPlayerCallback={(player: IPlayer) => onClickPlayerCallback && onClickPlayerCallback(player)}
                index={index}
              />
            );
          })}
      </SubstitutionsPlayersWrapper>
    </div>
  );
});
